import './Bot.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Banner,
  Button,
  Form,
  FormItem,
  Header,
  InfoBlock,
  List,
  ListItem,
  LottieIcon,
  Placeholder,
  Select,
} from '$uikit';
import { Loader } from '$shared/components';
import { useCurrentUserInfo, useTranslator, useWikiLink } from '$hooks';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { mainActions, mainSelector } from '$store/main';
import { ConnectBotSheet } from '$core/Bot/ConnectBotSheet/ConnectBotSheet';
import { IcNewTab28 } from '$assets';
import { VERIFICATION_APPLY, PERSONAL_SUBSCRIPTION_INFO } from '$shared/constants/pages';
import { BotCopyMenuButtonRow } from '$core/Bot/BotCopyMenuButtonRow/BotCopyMenuButtonRow';
import { _confirm } from '$utils/confirm';
import { batchActions } from '$store';
import { Link } from 'react-router5';
import { telegramActions } from '$store/telegram';

export const Bot: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [info, setInfo] = useState<{
    isActive: boolean;
    privacy: number;
  } | null>(null);
  const { hasBot } = useSelector(mainSelector);
  const user = useCurrentUserInfo();
  const wikiLinkViewMoreBots = useWikiLink('/telegram-bot/general-info-for-creators');

  const handleLoad = useCallback(() => {
    setLoadingStatus('loading');
    Api.get('/bot')
      .then((res) => {
        const data: any = res;
        setInfo(data);
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  }, []);

  useEffect(() => {
    handleLoad();
  }, [hasBot]);

  const handleConnect = useCallback(() => {
    // if (user.verified === 2) {
    //   window.open('https://t.me/TonPlaceModelsHelpBot', '_blank');
    // } else {
    //   dispatch(mainActions.setPopup(<ConnectBotSheet />));
    // }

    dispatch(mainActions.setPopup(<ConnectBotSheet />));
  }, [user.verified]);

  const handleRemove = useCallback(() => {
    _confirm(t('tg_bot_disable_confirm_title')).then((ok) => {
      if (!ok) {
        return;
      }

      dispatch(toastActions.setToastLoading());
      Api.post('/bot/remove')
        .then(() => {
          dispatch(
            batchActions(
              mainActions.setHasBot(false),
              telegramActions.setConnectedBot(false),
              toastActions.hideToast(),
            ),
          );
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    });
  }, []);

  const handlePrivacy = useCallback((e) => {
    const newVal = +e.target.value;
    const option = e.target.querySelector('option[value="' + newVal + '"]');
    _confirm(t('tg_bot_why_can_write_confirm_title') + ' ' + option.innerText).then(
      (ok) => {
        if (!ok) {
          return;
        }

        dispatch(toastActions.setToastLoading());
        Api.post('/bot/privacy', {
          privacy: newVal,
        })
          .then(() => {
            dispatch(toastActions.setToastSuccess());
            setInfo((old) => ({ ...old!, privacy: newVal }));
          })
          .catch((e) => {
            dispatch(toastActions.setToastFail(e.message));
          });
      },
    );
  }, []);

  function renderContent() {
    if (loadingStatus === 'error') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleLoad}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' || !info) {
      return <Loader isWrapped />;
    }

    if (!info.isActive) {
      return (
        <Placeholder
          centered
          iconSize={160}
          className="TelegramBotSheet__withoutBotPlaceholder"
          icon={<LottieIcon size={160} name="robot" />}
          title={t('tg_bot_placeholder_title')}
          actions={
            <>
              <Button stretched onClick={handleConnect}>
                {t('tg_bot_create_bot_button')}
              </Button>
              <Button
                href={wikiLinkViewMoreBots}
                target="_blank"
                mode="secondary"
                icon={<IcNewTab28 />}
                stretched
              >
                {t('tg_bot_why_needed')}
              </Button>
            </>
          }
        >
          {t('tg_bot_placeholder_desc')}
        </Placeholder>
      );
    }

    return (
      <>
        {!user.verified && (
          <Link routeName={VERIFICATION_APPLY} className="Bot__banner">
            <InfoBlock type="info" arrow>
              {t('tg_bot_go_verify_pay')}
            </InfoBlock>
          </Link>
        )}
        <Form>
          <FormItem title={t('tg_bot_why_can_write')}>
            <Select onChange={handlePrivacy} value={info.privacy}>
              <option value={0}>{t('tg_bot_why_can_write_type_all')}</option>
              <option value={1}>{t('tg_bot_why_can_write_type_sponsors')}</option>
              <option value={2}>{t('tg_bot_why_can_write_type_nobody')}</option>
            </Select>
          </FormItem>
          <FormItem
            title={t('tg_bot_sponsor_subscribe_title')}
            description={t('tg_bot_sponsor_subscribe_text')}
          >
            <List>
              <ListItem chevron routeName={PERSONAL_SUBSCRIPTION_INFO}>
                {t('tg_bot_sponsor_button_setting')}
              </ListItem>
            </List>
          </FormItem>
          {/*<BotCopyMenuButtonRow />*/}
          <FormItem
            title={t('tg_bot_disable_bot_title')}
            description={t('tg_bot_disable_bot_description')}
          >
            <List>
              <ListItem chevron onClick={handleRemove}>
                {t('tg_bot_disable_bot_button_disable')}
              </ListItem>
            </List>
          </FormItem>
          <FormItem>
            <Button
              mode="secondary"
              icon={<IcNewTab28 />}
              href={wikiLinkViewMoreBots}
              target="_blank"
            >
              {t('tg_bot_view_more_bots')}
            </Button>
          </FormItem>
        </Form>
      </>
    );
  }

  return (
    <>
      <Header showBackButton>{t('tg_bot_page_header')}</Header>
      {renderContent()}
    </>
  );
};
